import { useEffect, useMemo, useState } from "react"
import { GenerateWithModelLabel } from "@/components/media/generate-with-model-label"
import { Textarea } from "@/components/ui/primitives"
import { VoicePicker, characterVoiceSelection } from "@/components/voices/voice-picker"
import { useTranslation } from "@/hooks/use-translation"
import { confirmDelayKey } from "@/lib/confirm-delay-registry"
import type { VoiceSelection } from "@/lib/voices-api"
import type { TimelineLibraryVoiceRef, TimelineVoiceOption } from "./types"
import { AUDIO_COMPOSE_FIELD, AudioComposeDialog } from "./audio-compose-dialog"

type AudioTtsDialogProps = {
  projectId: number
  shotId: number
  voices: TimelineVoiceOption[]
  defaultLibraryVoice: TimelineLibraryVoiceRef | null
  /** Провайдер эффективной модели voice_tts. Список и дефолт — только он. */
  ttsProviderKind: string | null
  busy: boolean
  portalTarget: HTMLElement | null
  onClose: () => void
  onGenerate: (text: string, voice: VoiceSelection) => Promise<boolean>
}

function selectionProviderKind(selection: VoiceSelection, voices: TimelineVoiceOption[]): string | null {
  if (selection.kind === "library") return selection.provider_kind
  return voices.find((row) => row.kind === "voice" && row.voice_id === selection.voice_id)?.provider_kind ?? null
}

export function AudioTtsDialog({
  projectId,
  shotId,
  voices,
  defaultLibraryVoice,
  ttsProviderKind,
  busy,
  portalTarget,
  onClose,
  onGenerate,
}: AudioTtsDialogProps) {
  const { t } = useTranslation(["shot"])
  const [text, setText] = useState("")

  const defaultVoice = useMemo<VoiceSelection | null>(() => {
    for (const row of voices) {
      if (ttsProviderKind != null && row.provider_kind !== ttsProviderKind) continue
      const selection = characterVoiceSelection(row)
      if (selection) return selection
    }
    if (defaultLibraryVoice && (ttsProviderKind == null || defaultLibraryVoice.provider_kind === ttsProviderKind)) {
      return {
        kind: "library",
        provider_kind: defaultLibraryVoice.provider_kind,
        provider_voice_id: defaultLibraryVoice.provider_voice_id,
      }
    }
    return null
  }, [voices, defaultLibraryVoice, ttsProviderKind])
  const [voice, setVoice] = useState<VoiceSelection | null>(defaultVoice)
  useEffect(() => {
    setVoice((prev) => {
      if (prev && (ttsProviderKind == null || selectionProviderKind(prev, voices) === ttsProviderKind)) return prev
      return defaultVoice
    })
  }, [defaultVoice, ttsProviderKind, voices])

  const trimmed = text.trim()
  const disabledReason =
    trimmed.length === 0 ? t("timeline.ttsPlaceholder") : voice == null ? t("timeline.voice") : undefined

  return (
    <AudioComposeDialog
      title={t("timeline.tts")}
      closeLabel={t("timeline.cancel")}
      busy={busy}
      portalTarget={portalTarget}
      closeOnBackdrop={false}
      onClose={onClose}
    >
      <Textarea
        value={text}
        placeholder={t("timeline.ttsPlaceholder")}
        disabled={busy}
        autoFocus
        rows={5}
        onChange={(event) => setText(event.target.value)}
        className="w-full resize-none"
      />
      <VoicePicker
        projectId={projectId}
        value={voice}
        onChange={setVoice}
        characterVoices={voices}
        providerKinds={ttsProviderKind ? [ttsProviderKind] : undefined}
        disabled={busy || (voices.length === 0 && !defaultLibraryVoice)}
        className="w-full"
        buttonClassName={AUDIO_COMPOSE_FIELD}
        dropdownDirection="down"
        ariaLabel={t("timeline.voice")}
      />
      <div className="flex justify-end">
        <GenerateWithModelLabel
          label={t("timeline.generate")}
          busyLabel={t("timeline.generate")}
          busy={busy}
          disabled={trimmed.length === 0 || voice == null}
          disabledReason={disabledReason}
          size="sm"
          confirmDelayKey={confirmDelayKey("shot", shotId, "tts")}
          onClick={() => {
            if (!voice || !trimmed) return false
            void onGenerate(trimmed, voice).then((ok) => {
              if (ok) onClose()
            })
          }}
        />
      </div>
    </AudioComposeDialog>
  )
}
